import type { UnlockDef } from '../core/game/unlock-rules.js';
import { loadStickerSvg } from './stickers.js';
import { inlineSvg, packUrl } from './svg.js';

/**
 * A prize's own picture, for the two screens that show one.
 *
 * The reward screen's reveal and the Collection shelf both need "the picture of this prize", and they used
 * to hold a copy each of a five-line `defaultLoadHat`. That was fine while every prize was a hat and one
 * string replace answered the question; with cars and sticker sets in the table there is a *decision* in
 * here, and a decision written out twice is a decision that drifts.
 *
 * **The prize shows the thing itself**, never a category. That is the rule the hats-only table was built to
 * enforce - a signpost standing in for a route unlock was the weakest card the shelf ever had - and it
 * survives the rework: a car is the car's own outline, a sticker set is the stickers.
 *
 * Returns an element rather than an `<svg>`, because a set is three pictures and a card is one slot. Callers
 * only ever `replaceChildren` what they are given, so they neither know nor care how many are inside.
 *
 * Null is an expected outcome, not an error to handle: art that never lands leaves the card as it was.
 */
export function prizeArt(unlock: UnlockDef): Promise<HTMLElement | null> {
  switch (unlock.kind) {
    case 'vehicle':
      /*
       * The template doubles as its own thumbnail, exactly as it does on the chooser's cards: it is already
       * an uncoloured outline, which is the picture a colouring book would print - and on the shelf, dimmed
       * by `.is-locked`, an outline is precisely the silhouette a car she has not earned should be.
       */
      return group(unlock.grants.map((id) => inlineSvg(packUrl('vehicles', id))));
    case 'sticker-set':
      return group(unlock.grants.map(cloneSticker));
  }
}

/**
 * The set's stickers, side by side in one card.
 *
 * Three at a time is the point - a present rather than a trickle - so all three are shown at once rather
 * than one standing in for the set. Laid out by `.prize-art` in the stylesheet; nothing here knows how big
 * the card is.
 */
async function group(pending: readonly Promise<SVGSVGElement | null>[]): Promise<HTMLElement | null> {
  const art = (await Promise.all(pending)).filter((svg): svg is SVGSVGElement => svg !== null);
  if (art.length === 0) return null;

  const host = document.createElement('div');
  host.className = 'prize-art';
  host.setAttribute('aria-hidden', 'true');
  host.append(...art);
  return host;
}

/**
 * Cloned, which is `loadStickerSvg`'s contract: it parses each file once and hands the same element to
 * every caller, so appending it *moves* it out of wherever it already was - out of the tray, or out of the
 * shelf card the reveal is about to show the same sticker on.
 */
async function cloneSticker(id: string): Promise<SVGSVGElement | null> {
  const source = await loadStickerSvg(id);
  if (source === null) return null;

  const copy = source.cloneNode(true) as SVGSVGElement;
  copy.removeAttribute('width');
  copy.removeAttribute('height');
  copy.setAttribute('class', 'icon');
  return copy;
}
